import React, { useEffect, useContext } from "react";
import { Dialog } from "@mui/material";
import { calculateTimelineMessageDate } from "src/utils/dates";
import UserAvatar from "./UserAvatar";
import { useState } from "react";
import { UserKeys } from "src/constants/userDetails";
import { UserContext } from "src/scenes/App/UserContext";
import ChatBubbleActions from "./ChatBubbleActions";
import DownloadIcon from "src/img/chatBubbleActionIcons/download.svg";
import { MediaPreviewModal } from "./modals/MediaPreviewModal";
import { PlayIcon } from "src/components";
import FileIcon from "src/img/notice/document.svg";
import { ReplyMediaPreview } from "./MessageContentDisplay";
import { decryptEncryptedFile, fetchMatrixMedia, mxcToHttp } from "../utils";
import { ChatContext } from "../ChatContext";
import { LinkifiedMessage } from "../utils/LinkifiedMessage";
import { decryptEncryptedThumbnail } from "../utils";
import { getMatrixKeyRecoveryState, MATRIX_KEY_RECOVERY_STATE } from "../utils/matrixKeyRecovery";

import { RowContainer, Bubble, AvatarContainer,
    AvatarName, TimeStamp,
    ReplyFromContainer, EditedBadge,
    EditedBadgeContainer,
    BubbleAndActionContainerElement, DownloadWrapper,
    DownloadButton, StyledImage, PlayIconOverlay,
    StyledFileIcon, FileAttachment,
    IsRead, IsReadFlexContainer,
    DecryptionNoticeButton, DecryptionInfoBadge,
    DecryptionDialogContent
} from "./styled";

const DECRYPTION_FALLBACK_DETAIL = "This encrypted message could not be decrypted.";

const ChatBubble = (props) => {
    const { message, timestamp, photo, firstName, lastName, isChatByCurrentUser, currentMatrixUserUuid,
        isFirstOfConsecutive, isLastOfConsecutive, isSending, showTimeStamp, matrixEvent, 
        replyEvent, replyUserFullName, replyMessage, isEdited, downloadUrl, 
        isFile, hasBeenRead, usersProfile, senderDetails, showAvatar
    } = props;
    const currentUser = useContext(UserContext);
    const { useChatMessage: ctxUseChatMessage } = useContext(ChatContext);
    const { displayOtherUserProfileModal } = ctxUseChatMessage;

    const notDelivered = props.status === "failed" || props.isSending;
    const [messageToDisplay, setMessageToDisplay] = useState(message ?? "...");
    const [bubbleMediaUrl, setBubbleMediaUrl] = useState(downloadUrl); // decrypted or fallback
    const [replyMediaUrl, setReplyMediaUrl] = useState(null);

    const getDecryptedMediaUrl = async (event, client) => {
        if (!event || !client) return null;

        if (event.isEncrypted?.() && !event.isDecrypted?.()) {
            try {
                await client.decryptEventIfNeeded(event);
            } catch (err) {
                console.warn("Failed to decrypt event", err);
                return null;
            }
        }

        const content = event.getContent?.();
        const file = content?.file;
        if (!file?.url) return null;

        if (file.v === "v2") {
            try {
                const response = await fetchMatrixMedia(file.url, client);
                const blob = await response.blob();
                const decryptedBlob = await decryptEncryptedFile(blob, file, content?.info?.mimetype);
                return URL.createObjectURL(decryptedBlob);
            } catch (e) {
                console.error("Failed to decrypt file", e);
                return null;
            }
        }

        return mxcToHttp(file.url, client.baseUrl);
    };

    useEffect(() => {
        const objectUrls = [];
        let cancelled = false;
        const decryptMediaIfNeeded = async () => {
            const matrixClient = matrixEvent.getClient?.();
            if (!matrixClient) {
                return;
            }

            // Bubble's own file
            const bubbleUrl = await getDecryptedMediaUrl(matrixEvent, matrixClient);
            if (bubbleUrl) {
                objectUrls.push(bubbleUrl);
                if (!cancelled) {
                    setBubbleMediaUrl(bubbleUrl);
                }
            }

            // Reply's file
            if (replyEvent && !replyEvent.isRedacted?.()) {
                const replyUrl = await getDecryptedMediaUrl(replyEvent, matrixClient);
                if (replyUrl) {
                    objectUrls.push(replyUrl);
                    if (!cancelled) {
                        setReplyMediaUrl(replyUrl);
                    }
                }
            }
        };

        decryptMediaIfNeeded();
        return () => {
            cancelled = true;
            objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
        };
    }, [matrixEvent, replyEvent]);

    const determineBorderRadius = () => {
        let radius = "0.625rem";
        // Stand-alone message (both previous and next message is sent by another user)
        if (isFirstOfConsecutive && isLastOfConsecutive) {
            radius = isChatByCurrentUser ? "0.625rem 0.625rem 0 0.625rem" : "0.625rem 0.625rem 0.625rem 0";
        }
        // conditions below are for messages thread consecutively sent by the same user
        // the first of the messages thread
        else if (isFirstOfConsecutive && !isLastOfConsecutive) {
            radius = isChatByCurrentUser ? "0.625rem 0rem 0.625rem 0.625rem" : "0rem 0.625rem 0.625rem 0.625rem";
        }
        // the last message of the thread
        else if (isLastOfConsecutive && !isFirstOfConsecutive) {
            radius = isChatByCurrentUser ? "0.625rem 0.625rem 0 0.625rem" : "0.625rem 0.625rem 0.625rem 0rem";
        }
        // for threads that are between the first and last message
        else if (!isFirstOfConsecutive && !isLastOfConsecutive) {
            radius = isChatByCurrentUser ? "0.625rem 0rem 0rem 0.625rem" : "0rem 0.625rem 0.625rem 0rem";
        }
        /**
         * Note: these bubbles are displayed flex, column-reverse - see ChatMessages <MessagesContainer>
         * the first of the thread are at the bottom while the last are somewhere up top
         */
        return radius;
    };
    // This local echo work only on text messages, for the attachments see ChatRoomTimeline.
    const renderTimeStampContent = () => {
        if (isSending && !isFile) {
            return "Sending message...";
        } else {
            return timestamp ? calculateTimelineMessageDate(timestamp)["time"] : "";
        }
    };

    const stripMatrixReplyFallback = (body) => {
        if (!body) return body;
        return body
            .split("\n") 
            .filter(line => !line.startsWith(">"))
            .join("\n")
            .trim();
    };

    useEffect(() => {
        if (message !== undefined && typeof message === "string") {
            let cleaned = message;
            cleaned = stripMatrixReplyFallback(cleaned);
            if (cleaned.includes("Key backup is working, but we still do not (yet) have the key")) {
                cleaned = cleaned.replace(
                    "Key backup is working, but we still do not (yet) have the key",
                    "Key backup is working, but the key is unavailable"
                );
            }
            setMessageToDisplay(cleaned);
        }
    }, [message]);
    
    let someOneRepliedToAnEventMessage = firstName + " " + lastName + " replied to " + replyUserFullName;
    if (replyEvent && isChatByCurrentUser && replyEvent.getSender() === currentMatrixUserUuid) {
        someOneRepliedToAnEventMessage = "You replied to yourself";
    } else if (replyEvent && isChatByCurrentUser && replyEvent.getSender() !== currentMatrixUserUuid) {
        someOneRepliedToAnEventMessage = "You replied to " + replyUserFullName;
    } else if (replyEvent && matrixEvent.getSender() === replyEvent.getSender()) {
        someOneRepliedToAnEventMessage = replyUserFullName + " replied to themselves";
    } else if (replyEvent && replyEvent.getSender() === currentMatrixUserUuid) {
        someOneRepliedToAnEventMessage = firstName + " " + lastName + " replied to you";
    }

    return (
        <React.Fragment>
            <RowContainer isChatByCurrentUser={isChatByCurrentUser}>
                <div style={{ 
                    display: "flex", 
                    flexDirection: "column",
                    margin: "0 0.9375rem", 
                    padding: 0,
                    width: "100%"
                }}>
                    { showAvatar && 
                        <AvatarContainer isChatByCurrentUser={isChatByCurrentUser}>
                            <UserAvatar width="2.75rem" height="2.75rem" hideInfo={true}
                                photo={isChatByCurrentUser ? currentUser[UserKeys.PROFILE_PICTURE_URL] : photo}
                                firstName={firstName}
                                lastName={lastName}
                                statusBorder="2px solid #fff"
                                withShadow={false}
                                borderPixel="2px"
                                margin="0 0 0 0"
                                showPresenceStatus={false}
                                onClickUserAvatarFunction={!isChatByCurrentUser ? () => displayOtherUserProfileModal(senderDetails) : undefined}
                            />
                            <AvatarName>{ isChatByCurrentUser ? "Me" : firstName + " " + lastName }</AvatarName>
                        </AvatarContainer>
                    }

                    {replyEvent && !replyEvent.isRedacted?.() && (
                        <ReplyFromContainer isChatByCurrentUser={isChatByCurrentUser}>
                            <div className="replyFromFullName">{someOneRepliedToAnEventMessage}</div>
                            <div className="replyMessageContainer">
                                {(() => {
                                    const content = replyEvent.getContent?.();
                                    const msgtype = content?.msgtype;

                                    if ((msgtype === "m.image" || msgtype === "m.video" || msgtype === "m.file") && replyMediaUrl) {
                                        return (
                                            <ReplyMediaPreview
                                                type={msgtype}
                                                src={replyMediaUrl}
                                                value={content?.body}
                                                alt="reply preview"
                                                width={msgtype === "m.file" ? "auto" : "5rem"}
                                                height={msgtype === "m.file" ? "auto" : "4rem"}
                                                background={"#DBE5ED"}
                                                padding={"5px 10px"}
                                                isEditReply={false}
                                            />
                                        );
                                    }

                                    return <div className="replyFromMessage">{replyMessage}</div>;
                                })()}
                            </div>
                        </ReplyFromContainer>
                    )}
                    <BubbleAndActionContainer 
                        matrixEvent={matrixEvent}
                        isChatByCurrentUser={isChatByCurrentUser} 
                        computedRadius={determineBorderRadius()}
                        notDelivered={notDelivered}
                        messageToDisplay={messageToDisplay}
                        downloadUrl={bubbleMediaUrl}
                        decryptionFailure={getDecryptionFailureDetails(messageToDisplay, matrixEvent)}
                    />
                    {/* Show the edited word below and toggle. (ONLY if isEdited) */}
                    {isEdited && (
                        <EditedBadgeContainer isChatByCurrentUser={isChatByCurrentUser}>
                            <EditedBadge>
                                Edited
                            </EditedBadge>
                        </EditedBadgeContainer>
                    )}
                    <TimeStamp
                        isChatByCurrentUser={isChatByCurrentUser}
                        display={showTimeStamp ? "flex" : "none"}
                    >
                        { renderTimeStampContent() }
                    </TimeStamp>
                    <IsRead>
                        {hasBeenRead && isChatByCurrentUser && (
                            <IsReadFlexContainer>
                                {usersProfile.map(user => (
                                    <UserAvatar width="20px" height="20px" hideInfo={true}
                                        key={user.userId}
                                        isRead={true}
                                        photo={user.profilePictureUrl}
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                        statusBorder="2px solid #fff"
                                        withShadow={false}
                                        borderPixel="2px"
                                        margin="0 0 0 0"
                                        showPresenceStatus={false}
                                    />
                                ))} &nbsp; Read
                            </IsReadFlexContainer>
                        )}
                    </IsRead>
                </div>
            </RowContainer>
        </React.Fragment>
    );
};

const BubbleAndActionContainer = ({
    matrixEvent,
    isChatByCurrentUser,
    computedRadius,
    notDelivered,
    messageToDisplay,
    downloadUrl,
    decryptionFailure,
}) => {
    const content = matrixEvent?.getContent?.();

    const mimetype = content?.info?.mimetype;
    const isSvg = mimetype === "image/svg+xml";
    const msgtype = content?.msgtype;
    const matrixClient = matrixEvent.getClient?.();
    const isText = msgtype === "m.text" && !decryptionFailure;
    const isImage = !decryptionFailure && (msgtype === "m.image" || isSvg);
    const isVideo = !decryptionFailure && msgtype === "m.video";
    const isFile = !decryptionFailure && msgtype === "m.file" && !isImage && !isVideo;
    const isDownloadable = isImage || isVideo || isFile;
    const [thumbnailUrl, setThumbnailUrl] = useState(null);

    const fileName = content?.body;
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const handleFileClick = async () => {
        setIsPreviewOpen(true);
    };
    const contentUrl = content?.url || content?.file?.url;
    const oldThumbnailUrl = isVideo
        ? content?.info?.thumbnail_url
            ? mxcToHttp(content.info.thumbnail_url, matrixClient.baseUrl)
            : null
        : null;

    useEffect(() => {
        let cancelled = false;

        if (isVideo && content?.info?.thumbnail_file && matrixClient) {
            decryptEncryptedThumbnail(
                content.info.thumbnail_file,
                matrixClient
            )
                .then(url => {
                    if (!cancelled) setThumbnailUrl(url);
                })
                .catch(console.error);
        }

        return () => {
            cancelled = true;
        };
    }, [isVideo, content?.info?.thumbnail_file, matrixClient]);

    const commonBubbleProps = {
        isChatByCurrentUser,
        borderRadius: computedRadius,
        isMediaMessage: isImage || isVideo,
        isFileMessage: isFile,
        style: {
            opacity: notDelivered ? 0.5 : 1,
            order: isChatByCurrentUser ? 1 : 0,
            ...(isImage || isVideo ? { cursor: "pointer" } : {}),
            whiteSpace: "pre-wrap",
        },
    };

    return (
        <>
            <BubbleAndActionContainerElement isChatByCurrentUser={isChatByCurrentUser}>

                <DownloadWrapper>
                    {isDownloadable && downloadUrl && (
                        <DownloadButton
                            iconUrl={DownloadIcon}
                            onClick={async () => {
                                const response = await fetch(downloadUrl);
                                const blob = await response.blob();
                                const localUrl = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = localUrl;
                                a.download = fileName || "download";
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(localUrl);
                            }}
                            title={`Download ${fileName}`}
                            rel="noopener noreferrer"
                            target="_blank"
                        />
                    )}

                    {decryptionFailure ? (
                        <Bubble {...commonBubbleProps}>
                            <DecryptionFailureNotice
                                detail={decryptionFailure.detail}
                                isChatByCurrentUser={isChatByCurrentUser}
                                label={decryptionFailure.label}
                                title={decryptionFailure.title}
                            />
                        </Bubble>
                    ) : isVideo ? (
                        <Bubble {...commonBubbleProps} onClick={handleFileClick}>
                            <StyledImage
                                src={thumbnailUrl || oldThumbnailUrl}
                                alt={fileName}
                                maxWidth="12rem"
                                maxHeight="12rem"
                                style={{ cursor: "pointer", display: "block" }}
                            />
                            <PlayIconOverlay alt="Play Video" width="32px" height="24px">
                                <PlayIcon />
                            </PlayIconOverlay>
                        </Bubble>
                    ) : isSvg ? (
                        <Bubble {...commonBubbleProps} onClick={handleFileClick}>
                            <StyledImage
                                src={downloadUrl}
                                alt={fileName}
                                maxWidth="12rem"
                                maxHeight="12rem"
                            />
                        </Bubble>
                    ) : isImage ? (
                        <Bubble {...commonBubbleProps} onClick={handleFileClick}>
                            <StyledImage 
                                src={downloadUrl} 
                                alt={fileName} 
                                maxWidth="12rem" 
                                maxHeight="12rem"
                            />
                        </Bubble>
                    ) : isFile ? (
                        <FileAttachment onClick={handleFileClick}>
                            <StyledFileIcon src={FileIcon} alt="File" /> {messageToDisplay}
                        </FileAttachment>
                    ) : (
                        <Bubble {...commonBubbleProps}>
                            <LinkifiedMessage text={messageToDisplay} isChatByCurrentUser={isChatByCurrentUser} />
                        </Bubble>
                    )}

                </DownloadWrapper>

                <ChatBubbleActions
                    matrixEvent={matrixEvent}
                    isChatByCurrentUser={isChatByCurrentUser}
                    isText={isText}
                    fileUrl={downloadUrl}
                    isVideo={isVideo}
                />
            </BubbleAndActionContainerElement>
            <MediaPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                // For videos, pass contentUrl so the modal opens immediately (no large file fetch delay)
                // For other files, use downloadUrl for direct previewing
                url={isVideo ? contentUrl : downloadUrl}
                type={msgtype}
                fileName={fileName}
                baseUrl={matrixClient.baseUrl}
                thumbnailUrl={isVideo ? thumbnailUrl : null}
                // Actual playable video source for video since it did not pass it on url above
                videoUrl={downloadUrl}
            />
        </>
    );
};

const DecryptionFailureNotice = ({ detail, isChatByCurrentUser, label, title }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    return (
        <>
            <DecryptionNoticeButton
                type="button"
                isChatByCurrentUser={isChatByCurrentUser}
                onClick={() => setIsDialogOpen(true)}
                title="Show decryption details"
            >
                <span>{label}</span>
                <DecryptionInfoBadge>i</DecryptionInfoBadge>
            </DecryptionNoticeButton>
            <Dialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                aria-labelledby="decryption-failure-title"
            >
                <DecryptionDialogContent>
                    <h3 id="decryption-failure-title">{title}</h3>
                    <p>{detail || DECRYPTION_FALLBACK_DETAIL}</p>
                </DecryptionDialogContent>
            </Dialog>
        </>
    );
};

const getDecryptionFailureDetails = (message, matrixEvent) => {
    const content = matrixEvent?.getContent?.() || {};
    const isEncryptedEvent = matrixEvent?.getType?.() === "m.room.encrypted";
    const isUndecryptedEncryptedEvent = isEncryptedEvent && matrixEvent?.isDecrypted?.() !== true;
    const rawMessage = typeof message === "string" ? message : content?.body;
    const cleanedDetail = cleanDecryptionFailureDetail(rawMessage);
    const rawText = `${rawMessage || ""} ${matrixEvent?.decryptionFailureReason || ""}`;
    const hasDecryptionText =
        /unable to decrypt|decryptionerror|encrypted message is still syncing|key backup|key is unavailable|missingroomkey|unknown_inbound_session/i.test(rawText);
    const recoveryState = getMatrixKeyRecoveryState(matrixEvent);

    // Matrix retains the previous failure reason after a later key recovery. The
    // decrypted event content is authoritative once recovery succeeds.
    if (matrixEvent?.isDecrypted?.() === true) {
        return null;
    }

    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.RECOVERING) {
        return {
            detail: "Requesting the encryption key from your other devices and encrypted backup.",
            label: "Recovering encrypted message…",
            title: "Recovering message",
        };
    }
    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.WITHHELD) {
        return {
            detail: "The encryption key was permanently withheld from this device.",
            label: "Encrypted message unavailable",
            title: "Message cannot be recovered",
        };
    }
    if (recoveryState === MATRIX_KEY_RECOVERY_STATE.EXHAUSTED) {
        return {
            detail: "The encryption key was not found on another device or in your encrypted backup.",
            label: "Encrypted message could not be recovered",
            title: "Recovery attempts exhausted",
        };
    }

    if (!isUndecryptedEncryptedEvent && !hasDecryptionText) {
        return null;
    }

    return {
        detail: cleanedDetail || DECRYPTION_FALLBACK_DETAIL,
        label: "Unable to decrypt message",
        title: "Message could not be decrypted",
    };
};

const cleanDecryptionFailureDetail = (message) => {
    if (!message || typeof message !== "string") {
        return "";
    }

    let cleaned = message
        .replace(/^\*\*\s*/, "")
        .replace(/\s*\*\*$/, "")
        .replace(/^Unable to decrypt:\s*/i, "")
        .replace(/^DecryptionError:\s*/i, "")
        .trim();

    cleaned = cleaned.replace(
        "Key backup is working, but we still do not (yet) have the key",
        "Key backup is working, but the key is unavailable"
    );

    return cleaned;
};

export default ChatBubble;
